import { useUsersStore } from '@/store/users';
import { useUsersList } from '@/store/users/hooks';
import type { RoleKey, UserStatus } from '@/store/users/types';
import type { User } from '@/shared/types/user';
import { Icon } from '@iconify/react';
import {
  Autocomplete,
  AutocompleteItem,
  Button,
  Card,
  Spacer,
} from '@heroui/react';
import type { Key } from 'react';
import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { UserItem } from '../output/user';

export function UserSelect({
  label,
  roles,
}: {
  label?: string;
  roles?: RoleKey[];
}) {
  const users = useUsersList({ roles });

  return (
    <Autocomplete
      label={label}
      defaultItems={users}
      aria-label='Search for User'
    >
      {(item) => (
        <AutocompleteItem key={item.id} textValue={item.data.displayName}>
          <UserItem item={item} />
        </AutocompleteItem>
      )}
    </Autocomplete>
  );
}

export function UserMultiSelect({
  label,
  roles,
  status = 'active',
  initialSelected,
  onChange,
}: {
  label?: string;
  roles?: RoleKey[];
  status?: UserStatus;
  initialSelected?: string[];
  onChange?: (ids: string[]) => void;
}) {
  const users = useUsersList({ roles, status });
  const store = useUsersStore();
  const [editing, setEditing] = useState(!initialSelected?.length);
  const [selected, setSelected] = useState<string[]>(initialSelected || []);
  const availableUsers = useMemo(
    () => users.filter((item) => !selected.includes(item.id)),
    [users, selected],
  );
  const [key] = useState<string | null>(null);
  const [input, setInput] = useState<string>('');

  const updateSelected = (selected: string[]) => {
    setSelected(selected);
    onChange?.(selected);
  };

  const addKey = (key: string) => {
    if (key && !selected.includes(key)) {
      updateSelected([...selected, key]);
    }
  };

  const onRemove = (user: User) => {
    if (!user) {
      return;
    }

    const key = user.id;
    updateSelected(selected.filter((item) => item !== key));
  };

  return (
    // <div className='flex flex-col rounded-medium bg-default-100 px-3 pb-2 pt-2'>
    <Card className='border-1 border-gray-200 px-3 py-2' shadow='none'>
      {label && (
        <label className='text-[75%] font-medium text-foreground-600'>
          {label}
        </label>
      )}
      {selected.map((key) => {
        const user = store.state.users[key];

        return user ? (
          <div key={user.id} className='flex flex-row items-center'>
            <UserItem item={user} className='my-1 grow' />
            {editing && (
              <Button
                isIconOnly
                variant='flat'
                className='bg-transparent text-gray-500'
                onPress={() => onRemove(user)}
              >
                <Icon icon='mdi:close' />
              </Button>
            )}
          </div>
        ) : null;
      })}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, ease: 'easeInOut' }}
      >
        {editing ? (
          <div className='flex flex-row items-center'>
            <Autocomplete
              className='mb-2 mt-2'
              placeholder='Type to search...'
              defaultItems={availableUsers}
              // onSelectionChange={onChange}
              // onKeyDown={onKeyDown}
              selectedKey={key}
              onSelectionChange={(key: Key | null) => {
                if (!key) {
                  return;
                }

                addKey(key as string);
                setInput('');
              }}
              inputValue={input}
              onInputChange={(value: string) => setInput(value)}
              // inputValue={input}
              // selectorIcon={''}
              variant='flat'
              menuTrigger='input'
              // inputProps={{
              // classNames: {
              // inputWrapper: '!bg-transparent border-none p-1 shadow-none',
              // },
              // }}
            >
              {(item) => (
                <AutocompleteItem
                  key={item.id}
                  textValue={item.data.displayName}
                >
                  <UserItem item={item} />
                </AutocompleteItem>
              )}
            </Autocomplete>
            {selected.length ? (
              <Button
                isIconOnly
                variant='flat'
                className='bg-transparent'
                onPress={() => setEditing(false)}
              >
                <Icon icon='mdi:tick' className='text-success-600' />
              </Button>
            ) : (
              <div />
            )}
          </div>
        ) : (
          <div className='mb-2 mt-4 flex flex-row items-center'>
            <Spacer className='w-full' />
            <Button
              onPress={() => setEditing(true)}
              variant='light'
              className='text-gray-600'
              size='sm'
            >
              Edit
              <Icon
                icon='mdi:edit-outline'
                className='text-gray-600'
                width={28}
              />
            </Button>
          </div>
        )}
      </motion.div>
    </Card>
    // </div>
  );
}
